import { API_BASE } from './api.js';
import { deleteChunk, pendingUpload, saveChunk, type StoredChunk } from './chunkStore.js';

export interface ChunkQueueState {
  uploaded: number;
  pending: number;
  failed: number;
  /** True while a chunk has exhausted its fast retries and is being retried slowly. */
  stalled: boolean;
  online: boolean;
  lastError: string | null;
}

interface QueuedChunk {
  index: number;
  blob: Blob;
  attempts: number;
}

/** After this many tries the chunk is not dropped — it is retried at MAX_DELAY_MS. */
const STALL_AFTER_ATTEMPTS = 8;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;

export class CompleteMissingChunksError extends Error {
  constructor(public readonly missing: number[]) {
    super(`server is missing chunks: ${missing.join(', ')}`);
    this.name = 'CompleteMissingChunksError';
  }
}

/**
 * Uploads recording chunks one at a time, in order. A chunk is never thrown
 * away: once the fast retries are spent the queue keeps trying at the capped
 * backoff and reports `stalled` so the recorder can say so. Every chunk is
 * mirrored into IndexedDB until the server has acknowledged it.
 */
export class ChunkQueue {
  private queue: QueuedChunk[] = [];
  private running = false;
  private stopped = false;
  private uploaded = 0;
  private stalled = false;
  private lastError: string | null = null;
  /** Kept so a 409 missing_chunks can be answered without touching the disk. */
  private readonly sent = new Map<number, Blob>();

  constructor(
    private readonly meetingId: string,
    private readonly attempt: number,
    private readonly mimeType: string,
    private readonly onChange: (state: ChunkQueueState) => void,
  ) {
    window.addEventListener('online', this.handleOnline);
  }

  /** Rebuilds a queue from chunks an earlier session left in IndexedDB. */
  static async resume(
    meetingId: string,
    onChange: (state: ChunkQueueState) => void,
  ): Promise<{ queue: ChunkQueue; chunks: StoredChunk[] } | null> {
    const pending = await pendingUpload(meetingId);
    if (!pending || pending.chunks.length === 0) return null;
    const mimeType = pending.chunks[0]!.mimeType;
    const queue = new ChunkQueue(meetingId, pending.attempt, mimeType, onChange);
    for (const chunk of pending.chunks) queue.enqueue(chunk.index, chunk.blob);
    return { queue, chunks: pending.chunks };
  }

  private handleOnline = (): void => {
    this.emit();
    void this.drain();
  };

  private emit(): void {
    this.onChange({
      uploaded: this.uploaded,
      pending: this.queue.length,
      failed: 0,
      stalled: this.stalled,
      online: navigator.onLine,
      lastError: this.lastError,
    });
  }

  get uploadedCount(): number {
    return this.uploaded;
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  get attemptNumber(): number {
    return this.attempt;
  }

  /** Persists the chunk first, so a crash between here and the upload is survivable. */
  push(index: number, blob: Blob): void {
    this.sent.set(index, blob);
    void saveChunk({
      meetingId: this.meetingId,
      attempt: this.attempt,
      index,
      blob,
      mimeType: this.mimeType,
    });
    this.enqueue(index, blob);
  }

  private enqueue(index: number, blob: Blob): void {
    this.sent.set(index, blob);
    this.queue.push({ index, blob, attempts: 0 });
    this.emit();
    void this.drain();
  }

  private async putChunk(index: number, blob: Blob): Promise<void> {
    const res = await fetch(
      `${API_BASE}/meetings/${this.meetingId}/recording/chunks/${index}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': this.mimeType },
        body: blob,
      },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    await deleteChunk(this.meetingId, this.attempt, index);
  }

  private async drain(): Promise<void> {
    if (this.running || this.stopped) return;
    this.running = true;
    try {
      while (this.queue.length > 0 && !this.stopped) {
        const chunk = this.queue[0]!;
        if (!navigator.onLine) {
          this.emit();
          await delay(2000);
          continue;
        }
        try {
          await this.putChunk(chunk.index, chunk.blob);
          this.queue.shift();
          this.uploaded += 1;
          this.lastError = null;
          if (this.queue.length === 0) this.stalled = false;
          this.emit();
        } catch (err) {
          chunk.attempts += 1;
          this.lastError = err instanceof Error ? err.message : String(err);
          // Audio is never discarded: past the fast retries we simply slow down.
          if (chunk.attempts >= STALL_AFTER_ATTEMPTS) this.stalled = true;
          this.emit();
          await delay(Math.min(BASE_DELAY_MS * 2 ** chunk.attempts, MAX_DELAY_MS));
        }
      }
    } finally {
      this.running = false;
      this.emit();
    }
  }

  /** Resolves once the queue is empty or the timeout expires. */
  async flush(timeoutMs = 120_000): Promise<boolean> {
    const deadline = Date.now() + timeoutMs;
    void this.drain();
    while (this.queue.length > 0 && Date.now() < deadline && !this.stopped) {
      await delay(300);
      void this.drain();
    }
    return this.queue.length === 0;
  }

  private async postComplete(
    totalChunks: number,
    durationSec: number,
  ): Promise<{ ok: true; alreadyComplete?: boolean }> {
    const res = await fetch(`${API_BASE}/meetings/${this.meetingId}/recording/complete`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ totalChunks, durationSec }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      missing?: number[];
      alreadyComplete?: boolean;
    };
    if (res.status === 409 && body.error === 'missing_chunks') {
      throw new CompleteMissingChunksError(body.missing ?? []);
    }
    if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
    return { ok: true, ...(body.alreadyComplete ? { alreadyComplete: true } : {}) };
  }

  /**
   * Closes the recording. If the server reports gaps, the named chunks are
   * re-sent from the copies still in memory and the complete is retried.
   */
  async complete(
    totalChunks: number,
    durationSec: number,
    retries = 2,
  ): Promise<{ ok: true; alreadyComplete?: boolean }> {
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await this.postComplete(totalChunks, durationSec);
      } catch (err) {
        if (!(err instanceof CompleteMissingChunksError) || attempt >= retries) throw err;
        const resendable = err.missing.filter((i) => this.sent.has(i));
        if (resendable.length === 0) throw err;
        this.lastError = null;
        for (const index of resendable) this.enqueue(index, this.sent.get(index)!);
        await this.flush();
      }
    }
  }

  /** Called once the server owns the audio; drops the local copies. */
  async discardPersisted(): Promise<void> {
    for (const index of this.sent.keys()) {
      await deleteChunk(this.meetingId, this.attempt, index);
    }
    this.sent.clear();
  }

  dispose(): void {
    this.stopped = true;
    window.removeEventListener('online', this.handleOnline);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
